import { useEffect, useId, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { Tooltip } from "../../../components/Tooltip.jsx";
import { fmtZoneClock, fmtUtcClock, zoneCity, zoneOffset } from "./travelModel.js";
import { tx } from "./travelStyles.js";

// All labels use the parent's single minute-updating instant. The preference
// hook remains the sole owner of URL/account selection and saving.
export function TravelTimezone({ choices, choice, zone, now, loading, onSelect }) {
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const id = useId();
  const tooltipId = useId();
  const identity = choice === "local" ? `Local · ${zone ? zoneCity(zone) : "browser zone unavailable"}` : zone ? zoneCity(zone) : "Time zone unavailable";
  const label = loading ? "Loading your zone…" : zone ? `${fmtZoneClock(now, zone)} · ${identity} (${zoneOffset(zone, now)})` : identity;
  const close = (restore = false) => {
    setOpen(false);
    if (restore) trigger.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!open) return undefined;
    const selected = menu.current?.querySelector('[aria-checked="true"]') || menu.current?.querySelector("button");
    selected?.focus({ preventScroll: true });
    const outside = (e) => { if (!root.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  // Do not carry an open menu across an identity/preference reset.
  useEffect(() => { setOpen(false); }, [loading]);
  return <div ref={root} style={{ position: "relative", minWidth: 0, flex: "0 1 auto" }}
    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setOpen(false); setHelp(false); } }}>
    <button ref={trigger} type="button" data-testid="travel-tz"
      aria-label={`Time zone: ${label}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
      aria-describedby={help && zone && !open ? tooltipId : undefined} disabled={loading}
      onMouseEnter={() => setHelp(true)} onMouseLeave={() => setHelp(false)} onFocus={() => setHelp(true)}
      onClick={() => setOpen(v => !v)}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); setOpen(true); }
        if (e.key === "Escape") { close(); setHelp(false); }
      }}
      style={{ ...tx.box, padding: "0 9px", fontSize: T.fsSm, minHeight: 34, height: "auto", maxWidth: 300, minWidth: 0, textAlign: "left", lineHeight: 1.3 }}>
      <span data-testid={loading ? "travel-tz-loading" : "travel-tz-label"} style={{ padding: "5px 0" }}>{label}</span>
      <span aria-hidden="true">▾</span>
    </button>
    {help && zone && !open && <Tooltip id={tooltipId} style={{ position: "absolute", top: "100%", right: 0, whiteSpace: "nowrap", padding: "6px 10px", zIndex: 30 }}>{fmtUtcClock(now)} UTC</Tooltip>}
    {open && <div id={id} ref={menu} role="menu" aria-label="Time zones"
      onKeyDown={(e) => {
        const items = [...menu.current.querySelectorAll("button")];
        const i = items.indexOf(document.activeElement);
        const n = { ArrowDown: (i + 1) % items.length, ArrowUp: (i - 1 + items.length) % items.length, Home: 0, End: items.length - 1 }[e.key];
        if (n !== undefined) { e.preventDefault(); items[n]?.focus(); }
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); setHelp(false); }
      }}
      style={{ ...tx.card, position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 30, minWidth: 280, maxWidth: "calc(100vw - 32px)", maxHeight: 360, overflowY: "auto", padding: 5, boxShadow: T.projectsMenuShadow }}>
      {choices.map(c => <button key={c.value} type="button" tabIndex={-1} role="menuitemradio" aria-checked={choice === c.value}
        onClick={() => { onSelect(c.value); close(true); }}
        style={{ display: "flex", justifyContent: "space-between", gap: 12, width: "100%", border: 0, borderRadius: 6, padding: "9px 10px", font: "inherit", fontSize: T.fsSm, color: T.dark, background: choice === c.value ? T.blueSoft : T.card, cursor: "pointer", textAlign: "left" }}>
        <span>{c.label}</span><span style={{ whiteSpace: "nowrap" }}>{c.zone ? fmtZoneClock(now, c.zone) : "Unavailable"}</span>
      </button>)}
    </div>}
  </div>;
}
